import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '@/config/api';

interface MaintenanceModeContextType {
  isMaintenanceMode: boolean;
  setMaintenanceMode: (mode: boolean) => Promise<void>;
  isLoading: boolean;
}

const MaintenanceModeContext = createContext<MaintenanceModeContextType | undefined>(undefined);

export function MaintenanceModeProvider({ children }: { children: ReactNode }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial maintenance mode from API
  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.MAINTENANCE);
        
        // Check if we got HTML instead of JSON (local dev without API)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('API not available');
        }
        
        const data = await response.json();
        setIsMaintenanceMode(data.isMaintenanceMode ?? false);
      } catch (error) {
        console.log('API not available, using localStorage fallback');
        // Fallback to localStorage if API fails
        const saved = localStorage.getItem('maintenanceMode');
        setIsMaintenanceMode(saved === 'true');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenanceMode();
  }, []);

  // Also sync to localStorage as backup
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('maintenanceMode', String(isMaintenanceMode));
    }
  }, [isMaintenanceMode, isLoading]);

  const setMaintenanceMode = async (mode: boolean) => {
    try {
      const response = await fetch(API_ENDPOINTS.MAINTENANCE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isMaintenanceMode: mode }),
      });

      if (!response.ok) {
        throw new Error('Failed to update maintenance mode');
      }

      const data = await response.json();
      setIsMaintenanceMode(data.isMaintenanceMode);
    } catch (error) {
      console.error('Failed to update maintenance mode:', error);
      // Update locally even if API fails
      setIsMaintenanceMode(mode);
      localStorage.setItem('maintenanceMode', String(mode));
    }
  };

  return (
    <MaintenanceModeContext.Provider value={{ isMaintenanceMode, setMaintenanceMode, isLoading }}>
      {children}
    </MaintenanceModeContext.Provider>
  );
}

export function useMaintenanceMode() {
  const context = useContext(MaintenanceModeContext);
  if (context === undefined) {
    throw new Error('useMaintenanceMode must be used within a MaintenanceModeProvider');
  }
  return context;
}